import { BaseEntity } from '../../../Api/Types/baseTypes';
import { IdNameDto } from '../../Shared/Types/common';
import { GenericSearchFormProps } from '../../Shared/Components/SearchFormWrapper';
import { PaymentMethodType } from '../../Shared/Types/paymentMethodTypes';

export { PaymentMethodType } from '../../Shared/Types/paymentMethodTypes';

// Enums aligned with backend (TravelCRM.Domain.Enums)
// Backend sends these as camelCase strings via JsonStringEnumConverter
export enum InvoiceType {
  Standard = 'standard',
  Prepayment = 'prepayment',
  Final = 'final',
  Correction = 'correction',
  Refund = 'refund',
  Proforma = 'proforma',
}

// NEW: Invoice scheme (must stay numerically aligned with backend enum order)
// Backend sends these as camelCase strings via JsonStringEnumConverter
export enum InvoiceScheme {
  Standard = 'standard',
  TravelMargin = 'travelMargin',
}

export enum InvoiceStatus {
  Draft = 'draft',
  Issued = 'issued',
  Paid = 'paid',
  Cancelled = 'cancelled',
  Correction = 'correction',
}

export enum PrepaymentSettlementStatus {
  Pending = 'pending',
  Settled = 'settled',
  PartiallySettled = 'partiallySettled',
  Refunded = 'refunded',
}

/**
 * VAT rate type enum - synchronized with backend TravelCRM.Domain.Enums.VatRateType
 *
 * @remarks
 * - VatZW (0): Zero-rated / exempt supplies (Art. 43 ust. 1)
 * - VatNP (4): Not subject to VAT (Art. 113 ust. 1) - used for PEFRO refunds
 * - VatEU (6): Intra-EU supply (WDT)
 * - VatMargin (7): Travel margin scheme
 * - VatRehab (8): Rehabilitation services (exempt, Art. 43 ust. 1 pkt 18)
 */
export enum VatRateType {
  Vat23 = 'Vat23',
  Vat8 = 'Vat8',
  Vat5 = 'Vat5',
  VatZW = 'VatZW', // Changed from Vat0 (3)
  VatNP = 'VatNP', // Changed from VatNp (4) - PEFRO refunds
  VatExempt = 'VatExempt',
  VatEU = 'VatEU', // NEW (6): Intra-EU supply
  VatMargin = 'VatMargin',
  VatRehab = 'VatRehab', // NEW (8): Rehabilitation services
  Vat0Domestic = 'Vat0Domestic', // NEW (9): 0% domestic rate (KSeF: "0 KR")
}

// Items
export interface InvoiceItem {
  id?: number;
  name: string;
  description?: string;

  quantity: number;
  unitNet: number;
  unitGross: number;

  vatRate: number;
  vatType: VatRateType;
  unitVat: number;
  totalNet: number;
  totalVat: number;
  totalGross: number;

  isVatExempt: boolean;
  vatExemptNote?: string;
}

// Calculated item (kept separate for clarity, structure mirrors backend InvoiceItemCalculatedDto)
export interface InvoiceItemCalculated {
  id?: number;
  name: string;
  description?: string;
  quantity: number;
  unitNet: number;
  unitGross: number;
  vatRate: number;
  vatType: VatRateType;
  isVatExempt: boolean;
  vatExemptNote?: string;

  unitVat: number;
  totalNet: number;
  totalVat: number;
  totalGross: number;
}

// Main invoice DTO
export interface Invoice extends BaseEntity {
  invoiceNumber: string;
  issueDate: string; // DateTime
  saleDate: string; // DateTime
  placeOfIssue: string;

  type: InvoiceType;
  status: InvoiceStatus;
  scheme?: InvoiceScheme; // Invoice scheme (tax regime)

  // Seller
  sellerName: string;
  sellerStreetNo?: string; // Street and number (separate from address)
  sellerAddress: string;
  sellerNip: string;
  sellerBankAccount: string;

  // Buyer
  buyerName: string;
  buyerStreetNo?: string; // Street and number (separate from address)
  buyerAddress: string;
  buyerNip?: string;
  buyerCountryIso?: string;
  isBuyerVatEu?: boolean;
  buyerIsJst?: boolean;

  // Recipient (optional)
  recipientName?: string;
  recipientAddress?: string;
  recipientNip?: string;

  // Amounts / currency
  currency: string; // default "PLN" in backend
  currencyRate?: number;
  totalNet: number;
  totalVat: number;
  totalGross: number;
  amountInWords: string;

  // Payment
  paymentMethod: PaymentMethodType;
  bankAccount: string;
  paymentDueDate?: string; // DateTime?
  paidAmount: number;
  paidAt?: string; // DateTime?
  paymentStatus: string;
  paymentComment?: string;
  prepaymentsSettledAmount?: number;
  documentTotalAfterPrepayments?: number;
  importedReservationDirectAmount?: number;
  invoicePaymentsNetAmount?: number;
  hasPaymentHistory?: boolean;
  hasActiveCorrection?: boolean;
  isLegacySettlementRecord?: boolean;

  // PFRON Refund fields (only for InvoiceType.Refund)
  refundParticipantReferralCost?: number;
  refundGuardianReferralCost?: number;
  refundPfronSubsidyParticipant?: number;
  refundPfronSubsidyGuardian?: number;
  refundOwnContribution?: number;

  // NEW: PFRON fields for corrections (from backend)
  pfronRefundAmount?: number;
  pfronRefundPercentage?: number;
  pfronRefundNote?: string;

  // Relations
  reservationId?: number;
  companyId?: number;
  relatedContractNumber?: string;
  correctionInvoiceId?: number;

  // Correction invoice relations (NEW - Correction invoices)
  /**
   * Navigation property: List of correction invoices for this invoice.
   * Empty array for invoices that have not been corrected.
   */
  corrections?: Invoice[];

  /**
   * Navigation property: Original invoice that this correction refers to.
   * Null for non-correction invoices.
   */
  baseInvoice?: Invoice | null;

  // Prepayment relations (NEW - Final invoices)
  finalInvoiceId?: number;
  prepaymentInvoices?: Invoice[];
  remainingAmount?: number;
  settlementStatus?: PrepaymentSettlementStatus;

  // Notes / files
  legalNote?: string;
  comments?: string;
  notes?: string; // General notes field
  pdfFilePath?: string;
  createdAt: string; // DateTime

  // VAT display control (NEW - KROK 9.1)
  showVat?: boolean; // Determines whether VAT columns should be displayed in PDF (default: true)

  // Items
  items: InvoiceItem[];
}

// Search projection
export interface InvoiceSearch {
  id: number;
  invoiceNumber: string;
  issueDate: string; // DateTime
  totalAmount: number;
  currency?: string | null;
  reservationId: number;
  buyerName: string;
  recipientName?: string | null;

  applicantFirstName: string;
  applicantLastName: string;
  applicantEmail: string;
  applicantFullName: string;

  offerName: string;
  offerId?: number;

  bookingDateFrom?: string; // DateTime?
  bookingDateTo?: string; // DateTime?

  type: InvoiceType; // Invoice type (standard, prepayment, final, correction, refund, proforma)
  status: string;
  createdAt: string; // DateTime
}

// Filter selects
export interface InvoiceFilterSelectData {
  applicant: IdNameDto[];
  bookings: IdNameDto[];
  companies: IdNameDto[];
  invoiceStatuses: IdNameDto[];
  paidOptions: IdNameDto[];
}

// Polish labels for UI
// Removed labels and options for decoupling presentation from domain types

// Requests
export interface InvoicePagedRequest {
  reservationId?: number;
  applicantId?: number;
  companyId?: number;
  paidOption?: string;
  status?: string;
  issueDateFrom?: string;
  issueDateTo?: string;
  invoiceNumber?: string;
  applicantFirstName?: string;
  applicantLastName?: string;
  applicantEmail?: string;
  totalAmountFrom?: number;
  totalAmountTo?: number;
  searchTerm?: string;
  pageNumber?: number; // default 1 on backend
  pageSize?: number; // default 10 on backend
}

export interface NextInvoiceNumberRequest {
  type: InvoiceType;
  companyId?: number; // Required for per-company invoice numbering
  issueDate?: string; // Optional: for date-based numbering (e.g., monthly counters)
}

// UI search form types (align with Offers pattern)
export interface InvoiceSearchFilters {
  invoiceNumber: string;
  applicantEmail: string;
  applicantFirstName: string;
  applicantLastName: string;
  issueDateFrom: string;
  issueDateTo: string;
  totalAmountFrom: string;
  totalAmountTo: string;
  reservationId: string;
  applicantId: string;
  companyId: string;
  paidOption: string;
  status: string;
  searchTerm: string;
}
export type InvoiceSearchFormProps = GenericSearchFormProps<InvoiceSearchFilters>;

// -------- Enums metadata endpoints (schemes / enums) --------
// Keep TS shapes aligned with backend DTOs (camelCase JSON serialization)

export interface EnumOptionDto<T extends string> {
  value: T;
  label: string;
}

export interface InvoiceSchemeInfo {
  scheme: InvoiceScheme | string | number;
  schemeLabel: string;
}

export interface AllowedTypesForScheme {
  scheme: InvoiceScheme | string | number;
  schemeLabel: string;
  allowedTypes: EnumOptionDto<InvoiceType>[];
}

export interface EnumValueDto {
  id: number;
  name: string;
}

export interface InvoiceSchemeAllowedMatrix {
  schemeId: number;
  scheme: string;
  allowedTypes: EnumValueDto[];
}

export interface InvoiceEnums {
  types: EnumValueDto[];
  statuses: EnumValueDto[];
  schemes: EnumValueDto[];
  paymentMethods: EnumValueDto[];
  schemeTypeMatrix: InvoiceSchemeAllowedMatrix[];
}
